import { Component, computed, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ActivatedRoute } from '@angular/router';
import { combineLatest, of, switchMap, tap } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { CatalogProduct } from '../../core/models/catalog.model';
import { CartService } from '../../core/services/cart.service';
import { CATALOG_PAGE_SIZE, CatalogService } from '../../core/services/catalog.service';
import { OverlayService } from '../../core/services/overlay.service';
import { ProductCardComponent } from '../../shared/product-card/product-card.component';

/**
 * Storefront shop — same data flow as HVAC `CatalogComponent`:
 * route `/shop/:categorySlug` + `?brand=` / `?q=` → API `getPaged` (DB filter) + load more.
 */
@Component({
  selector: 'app-collections',
  imports: [ProductCardComponent],
  templateUrl: './collections.component.html'
})
export class CollectionsComponent {
  private readonly cart = inject(CartService);
  private readonly overlay = inject(OverlayService);
  private readonly catalog = inject(CatalogService);
  private readonly route = inject(ActivatedRoute);

  readonly items = signal<CatalogProduct[]>([]);
  readonly totalCount = signal(0);
  readonly page = signal(1);
  readonly pageSize = CATALOG_PAGE_SIZE;
  readonly loading = signal(true);
  readonly loadingMore = signal(false);

  private categorySlug: string | null = null;
  private brandId: string | undefined;
  private search: string | undefined;
  private requestId = 0;

  readonly hasMore = computed(() => this.items().length < this.totalCount());

  constructor() {
    combineLatest([this.route.paramMap, this.route.queryParamMap])
      .pipe(
        tap(() => {
          this.loading.set(true);
          this.page.set(1);
          this.items.set([]);
        }),
        switchMap(([params, qp]) => {
          this.categorySlug = params.get('categorySlug');
          this.brandId = qp.get('brand')?.trim() || undefined;
          this.search = qp.get('q')?.trim() || undefined;

          const requestId = ++this.requestId;
          return this.catalog
            .queryProducts({
              categorySlug: this.categorySlug,
              brandId: this.brandId,
              search: this.search,
              page: 1,
              pageSize: this.pageSize
            })
            .pipe(
              catchError(() =>
                of({ items: [] as CatalogProduct[], totalCount: 0, page: 1, pageSize: this.pageSize })
              ),
              tap((page) => {
                if (requestId !== this.requestId) {
                  return;
                }
                this.items.set(page.items);
                this.totalCount.set(page.totalCount);
                this.loading.set(false);
                this.loadingMore.set(false);
              })
            );
        }),
        takeUntilDestroyed()
      )
      .subscribe();
  }

  loadMore(): void {
    if (!this.hasMore() || this.loading() || this.loadingMore()) {
      return;
    }
    const nextPage = this.page() + 1;
    const requestId = ++this.requestId;
    this.loadingMore.set(true);
    this.catalog
      .queryProducts({
        categorySlug: this.categorySlug,
        brandId: this.brandId,
        search: this.search,
        page: nextPage,
        pageSize: this.pageSize
      })
      .subscribe({
        next: (page) => {
          if (requestId !== this.requestId) {
            return;
          }
          this.page.set(nextPage);
          this.items.update((list) => [...list, ...page.items]);
          this.totalCount.set(page.totalCount);
          this.loadingMore.set(false);
        },
        error: () => {
          if (requestId !== this.requestId) {
            return;
          }
          this.loadingMore.set(false);
        }
      });
  }

  add(product: CatalogProduct): void {
    this.cart.add(product);
    this.overlay.openCart();
  }
}
